import React, { useState, useRef } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import TotalModalWrapper from '../../Model/Share/Normal/TotalModalWrapper';

const PIN_LENGTH = 4;

export default function MainSecondPasswordEditModal(props) {
  const { onClickExit, onClickSave } = props;

  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [currentPin, setCurrentPin] = useState(Array(PIN_LENGTH).fill(''));
  const [newPin, setNewPin] = useState(Array(PIN_LENGTH).fill(''));
  const [confirmPin, setConfirmPin] = useState(Array(PIN_LENGTH).fill(''));

  const isNewFilled = newPin.every(v => v !== '');
  const isConfirmFilled = confirmPin.every(v => v !== '');
  const isMatch = isNewFilled && isConfirmFilled && newPin.join('') === confirmPin.join('');
  const isCurrentFilled = currentPin.every(v => v !== '');
  const canSave = isCurrentFilled && isNewFilled && isConfirmFilled && isMatch;

  // 부모가 API 호출 후 성공 시 모달을 닫음 (콜백 미지정 시는 즉시 닫기) //
  function onClickSaveButton() {
    if (!canSave) return;

    if (onClickSave) {
      onClickSave(currentPin.join(''), newPin.join(''));
      return;
    }
    
    onClickExit();
  }

  // 버튼 라벨 결정 //
  function decideSaveButtonLabel() {
    if (isNewFilled && isConfirmFilled && !isMatch) return t('myStoreSetting.account.secondPasswordEditModal.mismatch');

    return t('myStoreSetting.account.secondPasswordEditModal.save');
  }

  return (
    <TotalModalWrapper>
      <Modal backgroundColor={theme.subBackground}>
        <HeaderRow>
          <HeaderColumn>
            <Text
              fontFamily='NotoSansKR-Bold'
              fontSize={16}
              color={theme.text}>
              {t('myStoreSetting.account.secondPasswordEditModal.title')}
            </Text>
            <Text
              fontFamily='NotoSansKR-Regular'
              fontSize={13}
              color={theme.gray}>
              {t('myStoreSetting.account.secondPasswordEditModal.description')}
            </Text>
          </HeaderColumn>
          <CancelChip backgroundColor={theme.buttonFill} onClick={onClickExit}>
            <Text
              fontFamily='NotoSansKR-Medium'
              fontSize={13}
              color={theme.text}>
              {t('myStoreSetting.account.secondPasswordEditModal.cancel')}
            </Text>
          </CancelChip>
        </HeaderRow>
        <PinFieldGroup
          label={t('myStoreSetting.account.secondPasswordEditModal.currentLabel')}
          values={currentPin}
          setValues={setCurrentPin}
          theme={theme} />
        <PinFieldGroup
          label={t('myStoreSetting.account.secondPasswordEditModal.newLabel')}
          values={newPin}
          setValues={setNewPin}
          theme={theme} />
        <PinFieldGroup
          label={t('myStoreSetting.account.secondPasswordEditModal.confirmLabel')}
          values={confirmPin}
          setValues={setConfirmPin}
          theme={theme} />
        <StateButton
          gradient={theme.primaryGradient}
          onClick={onClickSaveButton}>
          <Text
            fontFamily='NotoSansKR-Bold'
            fontSize={15}
            color={theme.subBackground}>
            {decideSaveButtonLabel()}
          </Text>
        </StateButton>
      </Modal>
    </TotalModalWrapper>
  );
}

// PIN 필드 한 세트 (라벨 + 4셀) //
function PinFieldGroup(props) {
  const { label, values, setValues, theme } = props;

  const inputRefs = useRef([]);

  function onChangeCell(idx, value) {
    const cleaned = value.replace(/[^0-9]/g, '').slice(-1);

    const next = [...values];
    next[idx] = cleaned;
    setValues(next);

    if (cleaned !== '' && idx < PIN_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  }

  function onKeyDownCell(idx, e) {
    if (e.key === 'Backspace' && values[idx] === '' && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  }

  return (
    <FieldGroup>
      <Text
        fontFamily='NotoSansKR-Medium'
        fontSize={14}
        color={theme.text}>
        {label}
      </Text>
      <PinRow>
        {values.map((value, idx) => (
          <PinCell
            key={'PinCell-' + label + '-' + idx}
            ref={(el) => (inputRefs.current[idx] = el)}
            borderColor={theme.primary}
            color={theme.primary}
            type='tel'
            inputMode='numeric'
            maxLength={1}
            value={value}
            onChange={(e) => onChangeCell(idx, e.target.value)}
            onKeyDown={(e) => onKeyDownCell(idx, e)} />
        ))}
      </PinRow>
    </FieldGroup>
  );
}

const Modal = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 16px;

  width: 305px;
  padding: 20px;
  box-sizing: border-box;

  border-radius: 15px;
  background-color: ${props => props.backgroundColor};
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-start;

  gap: 10px;

  width: 100%;
`;

const HeaderColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 4px;
`;

const CancelChip = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 62px;
  height: 40px;

  padding: 4px 10px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  width: 100%;
`;

const PinRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  gap: 8px;

  width: 100%;
`;

const PinCell = styled.input`
  flex: 0 0 60px;

  width: 60px;
  height: 60px;
  min-width: 0;
  box-sizing: border-box;

  border-radius: 10px;
  border: 2px solid ${props => props.borderColor};

  outline: none;

  background-color: transparent;
  color: ${props => props.color};

  font-family: NotoSansKR-Bold;
  font-size: 20px;

  text-align: center;
`;

const StateButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 48px;

  border-radius: 10px;
  background: ${props => props.gradient};

  margin-top: 8px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};
  background-color: ${props => props.backgroundColor || 'transparent'};

  margin: 0 0 0 0;
`;
